import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, Loader2, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAvailability, createBooking } from "@/lib/api";
import { SERVICES } from "@/lib/content";

const ORG_TYPES = ["Government agency", "Small business", "Nonprofit organization", "Healthcare organization", "Professional services", "Other"];

const toISODate = (d) => {
  // Preserve local calendar date (avoid UTC shift from toISOString())
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtDate = (d) =>
  d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

const fmtSlot = (t) => {
  const [h, m] = t.split(":").map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

export default function BookingWidget() {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const [step, setStep] = useState(1); // 1: date+time, 2: details, 3: success
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "",
    organization_type: "", service_interest: "", notes: "",
  });
  const [confirmed, setConfirmed] = useState(null);

  const isoDate = date ? toISODate(date) : "";

  useEffect(() => {
    if (!date) { setSlots([]); return; }
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedTime("");
    getAvailability(toISODate(date))
      .then((res) => { if (!cancelled) setSlots(res.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [date]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !selectedTime) { toast.error("Please pick a date and time."); return; }
    setSubmitting(true);
    try {
      const booking = await createBooking({
        ...form,
        slot_date: isoDate,
        slot_time: selectedTime,
        duration_minutes: 30,
      });
      setConfirmed(booking);
      setStep(3);
      toast.success("Consultation booked. A confirmation is on the way.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Booking failed. Please try another slot.";
      toast.error(detail);
      // Refresh availability if the slot was taken
      if (err?.response?.status === 409) {
        try {
          const res = await getAvailability(isoDate);
          setSlots(res.slots || []);
          setSelectedTime("");
        } catch {}
      }
    } finally { setSubmitting(false); }
  };

  const bookAnother = () => {
    setDate(null); setSlots([]); setSelectedTime("");
    setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", organization_type: "", service_interest: "", notes: "" });
    setConfirmed(null);
    setStep(1);
  };

  // ---------- STEP 3: success ----------
  if (step === 3 && confirmed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="booking-success"
        className="border border-gray-200 bg-white p-10 md:p-14"
      >
        <div className="w-14 h-14 bg-gold grid place-items-center">
          <CheckCircle2 className="w-7 h-7 text-white" />
        </div>
        <div className="overline mt-8 mb-3">Consultation Booked</div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal leading-tight">
          You're on the calendar, {confirmed.first_name}.
        </h2>
        <p className="mt-4 text-charcoal-soft max-w-xl leading-relaxed">
          A confirmation has been logged and our team will reach out to {confirmed.email} shortly with a calendar invite and the meeting link.
        </p>

        <dl className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border border-gray-200">
          <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
            <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-soft">Date</dt>
            <dd className="mt-2 font-heading text-lg text-charcoal">{fmtDate(new Date(confirmed.slot_date + "T00:00"))}</dd>
          </div>
          <div className="p-6 border-b md:border-b-0 md:border-r border-gray-200">
            <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-soft">Time (Central)</dt>
            <dd className="mt-2 font-heading text-lg text-charcoal">{fmtSlot(confirmed.slot_time)}</dd>
          </div>
          <div className="p-6">
            <dt className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-soft">Duration</dt>
            <dd className="mt-2 font-heading text-lg text-charcoal">{confirmed.duration_minutes} minutes</dd>
          </div>
        </dl>

        <div className="mt-10 flex flex-wrap gap-3">
          <button
            onClick={bookAnother}
            data-testid="booking-book-another"
            className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-5 py-3 text-sm hover:bg-charcoal hover:text-white transition-colors"
          >
            Book another <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div>
      {/* Stepper */}
      <div className="flex items-center gap-4 mb-8">
        {[
          { n: 1, label: "Date & Time" },
          { n: 2, label: "Your details" },
        ].map((s, i, arr) => (
          <div key={s.n} className="flex items-center gap-3">
            <div className={`w-8 h-8 grid place-items-center text-sm font-mono ${
              step === s.n ? "bg-gold text-white" : step > s.n ? "bg-charcoal text-gold" : "bg-gray-100 text-charcoal-soft"
            }`}>
              {step > s.n ? "✓" : s.n}
            </div>
            <div className={`text-sm font-medium ${step === s.n ? "text-charcoal" : "text-charcoal-soft"}`}>{s.label}</div>
            {i < arr.length - 1 && <div className="w-8 h-px bg-gray-300 mx-2" />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.3 }}
            className="grid lg:grid-cols-2 gap-6"
            data-testid="booking-step-1"
          >
            {/* Calendar */}
            <div className="border border-gray-200 p-6 bg-white">
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays className="w-4 h-4 text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-soft">Choose a date</span>
              </div>
              <Calendar
                data-testid="booking-calendar"
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => {
                  const t = new Date(); t.setHours(0, 0, 0, 0);
                  return d < t || d.getDay() === 0 || d.getDay() === 6;
                }}
                className="w-full"
                classNames={{
                  months: "flex flex-col w-full",
                  month: "w-full space-y-4",
                  caption: "flex justify-between pt-1 relative items-center",
                  caption_label: "font-heading text-base text-charcoal",
                  nav_button: "h-8 w-8 border border-gray-200 hover:border-gold hover:text-gold transition-colors grid place-items-center",
                  table: "w-full border-collapse",
                  head_cell: "text-[10px] font-mono uppercase tracking-[0.16em] text-charcoal-soft w-10 h-10",
                  cell: "w-10 h-10 text-center text-sm p-0",
                  day: "w-10 h-10 hover:bg-offwhite transition-colors",
                  day_selected: "bg-charcoal text-gold hover:bg-charcoal hover:text-gold",
                  day_today: "border border-gold text-charcoal",
                  day_disabled: "text-gray-300 cursor-not-allowed hover:bg-transparent",
                  day_outside: "text-gray-300",
                }}
              />
              <p className="mt-4 text-xs text-charcoal-soft">
                Business hours: Monday – Friday, 9:00 AM – 5:00 PM Central Time.
              </p>
            </div>

            {/* Slots */}
            <div className="border border-gray-200 p-6 bg-white flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-soft">Choose a time</span>
              </div>

              {!date && (
                <div className="flex-1 grid place-items-center text-center text-charcoal-soft">
                  <div>
                    <div className="w-14 h-14 mx-auto border-2 border-dashed border-gray-300 grid place-items-center">
                      <CalendarDays className="w-6 h-6" />
                    </div>
                    <p className="mt-4 text-sm">Pick a date to see available times.</p>
                  </div>
                </div>
              )}

              {date && loadingSlots && (
                <div className="flex-1 grid place-items-center text-charcoal-soft">
                  <Loader2 className="w-6 h-6 animate-spin text-gold" />
                </div>
              )}

              {date && !loadingSlots && (
                <>
                  <div className="text-sm font-medium text-charcoal mb-4">{fmtDate(date)}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {slots.map((s) => {
                      const selected = selectedTime === s.time;
                      const disabled = !s.available;
                      return (
                        <button
                          key={s.time}
                          type="button"
                          disabled={disabled}
                          onClick={() => setSelectedTime(s.time)}
                          data-testid={`slot-${s.time}`}
                          className={`px-3 py-3 text-sm border transition-colors ${
                            selected
                              ? "bg-charcoal text-gold border-charcoal"
                              : disabled
                                ? "border-gray-100 text-gray-300 cursor-not-allowed line-through"
                                : "border-gray-300 text-charcoal hover:border-gold hover:text-gold"
                          }`}
                        >
                          {fmtSlot(s.time)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="mt-auto pt-6 flex justify-end">
                <button
                  type="button"
                  disabled={!date || !selectedTime}
                  onClick={() => setStep(2)}
                  data-testid="booking-continue"
                  className="inline-flex items-center gap-2 bg-gold text-white px-6 py-3 text-sm font-medium hover:bg-gold-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.form
            key="step2"
            onSubmit={submit}
            initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.3 }}
            className="border border-gray-200 p-8 bg-white"
            data-testid="booking-step-2"
          >
            {/* Selected summary */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-6 border-b border-gray-200">
              <div>
                <div className="overline mb-1">Your appointment</div>
                <div className="font-heading text-lg text-charcoal">
                  {fmtDate(date)} · {fmtSlot(selectedTime)} CT · 30 min
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="booking-back"
                className="inline-flex items-center gap-2 text-sm text-charcoal-soft hover:text-gold transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Change
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="First name" name="first_name" value={form.first_name} onChange={onChange} required />
              <Field label="Last name" name="last_name" value={form.last_name} onChange={onChange} required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
              <Field label="Phone number" name="phone" value={form.phone} onChange={onChange} />
              <Field label="Company / Organization" name="company" value={form.company} onChange={onChange} />

              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">Which best describes you</label>
                <Select value={form.organization_type} onValueChange={(v) => setForm({ ...form, organization_type: v })}>
                  <SelectTrigger data-testid="booking-org-type" className="w-full h-[50px] rounded-none border border-gray-300 focus:ring-2 focus:ring-gold px-4">
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">Service interest</label>
                <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                  <SelectTrigger data-testid="booking-service-interest" className="w-full h-[50px] rounded-none border border-gray-300 focus:ring-2 focus:ring-gold px-4">
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => <SelectItem key={s.slug} value={s.label}>{s.label}</SelectItem>)}
                    <SelectItem value="Other / Not sure yet">Other / Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">Anything we should know before the call</label>
                <textarea
                  name="notes"
                  rows={4}
                  value={form.notes}
                  onChange={onChange}
                  data-testid="booking-notes"
                  className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
                  placeholder="A brief description of your organization or the challenge you're facing."
                />
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-charcoal-soft max-w-md">
                Your booking is confidential and will be reviewed by Groman Associates leadership.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="inline-flex items-center gap-2 bg-gold text-white px-8 py-4 text-sm font-medium hover:bg-gold-hover transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                {submitting ? "Booking…" : "Confirm Consultation"}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`booking-${name}`}
        className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
      />
    </div>
  );
}
